import { type ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { useStore } from '@hooks/useStore';
import { authApi } from '@/api-client/auth';
import {
  isTrustedOAuthAuthorizeReturnUrl,
  readOAuthAuthorizeRedirectFromSearch,
  readPostLoginRedirectFromSearch
} from '@utils/oauthPostLoginRedirect';
import LoginStep from './LoginStep';
import MfaStep from './MfaStep';
import SelectTenancyStep from './SelectTenancyStep';
import SignInStep from './SignInStep';
import { Spinner } from 'stubben-edge-unified-frontend-react-components';

const loginBackgroundStyle = {
  backgroundImage: 'url(/images/background.svg)',
  backgroundPosition: 'center',
  backgroundSize: 'cover'
} as const;

function LoginPageLayout({ children }: { children: ReactNode }) {
  return (
    <div
      className="h-full min-h-screen bg-no-repeat"
      style={loginBackgroundStyle}
    >
      <div className="flex p-10">
        <img src="/images/flightdeck.svg" alt="Flightdeck" />
      </div>
      <div className="p-10 lg:p-20">
        <div className="mx-auto max-w-[30rem]">{children}</div>
      </div>
    </div>
  );
}

const LoginPage = () => {
  const { authStore } = useStore();
  const { token, isAuthed } = authStore;
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [mfaOption, setMfaOption] = useState<string>('');
  const [bearerToken, setBearerToken] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [tenants, setTenants] = useState<any>();
  const [company, setCompany] = useState<any>();
  const [ssoHandoffFailed, setSsoHandoffFailed] = useState(false);

  const apiUrl = import.meta.env.DEV ? '/api' : import.meta.env.VITE_API_URL;

  const oauthReturn = readOAuthAuthorizeRedirectFromSearch();
  const trustedOAuthReturn =
    oauthReturn && isTrustedOAuthAuthorizeReturnUrl(oauthReturn)
      ? oauthReturn
      : null;
  const portalReturn = readPostLoginRedirectFromSearch();

  // Show inactivity message if redirected due to session timeout (unauthenticated only)
  useEffect(() => {
    if (isAuthed || token) {
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const reason = searchParams.get('reason');
    if (reason === 'inactivity') {
      toast.info('You were logged out due to inactivity.', {
        style: {
          fontSize: '1rem',
          fontWeight: 700,
          color: '#111827',
          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.24)',
          fontFamily:
            "Inter, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
        }
      });
      // Clean the query param to avoid repeated toasts on refresh
      searchParams.delete('reason');
      const newQuery = searchParams.toString();
      const newUrl = `${window.location.pathname}${
        newQuery ? `?${newQuery}` : ''
      }`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [isAuthed, token]);

  // SSO hand-off (e.g. PBv2 configurator): an authenticated user arriving with a
  // trusted ?redirect= to the CRM /oauth/authorize endpoint must continue that
  // flow, not go to the dashboard. The API needs a Passport web session before
  // it will honour the authorize request, so establish it first.
  useEffect(() => {
    if (!isAuthed || !trustedOAuthReturn || ssoHandoffFailed) {
      return;
    }

    authApi
      .establishPassportWebSession()
      .then(() => {
        window.location.assign(trustedOAuthReturn);
      })
      .catch((e) => {
        console.error('SSO hand-off: establishPassportWebSession failed:', e);
        // Fall back to the dashboard rather than bouncing between the
        // portal and the API login redirect forever.
        setSsoHandoffFailed(true);
      });
  }, [isAuthed, trustedOAuthReturn, ssoHandoffFailed]);

  if (isAuthed) {
    // Keep the current URL (and its ?redirect= param) alive while the SSO
    // hand-off effect runs; navigating away here would drop the param before
    // handleLoginResponse can read it.
    if (trustedOAuthReturn && !ssoHandoffFailed) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50">
          <Spinner width={80} height={80} />
        </div>
      );
    }

    if (portalReturn?.kind === 'portal') {
      return <Navigate to={portalReturn.target} replace />;
    }

    return <Navigate to="/dashboard" replace />;
  }

  const stepComponents = [
    {
      component: (
        <LoginStep
          title="Log in"
          currentStepIndex={currentStepIndex}
          setCurrentStepIndex={setCurrentStepIndex}
          setEmail={setEmail}
          setMfaOption={setMfaOption}
          setBearerToken={setBearerToken}
          setTenants={setTenants}
          setCompany={setCompany}
        />
      )
    },
    {
      component: (
        <MfaStep
          apiUrl={apiUrl}
          title="Multi-factor authentication"
          currentStepIndex={currentStepIndex}
          setCurrentStepIndex={setCurrentStepIndex}
          mfaOption={mfaOption}
          bearerToken={bearerToken}
          setBearerToken={setBearerToken}
          setTenants={setTenants}
          setCompany={setCompany}
        />
      )
    },
    {
      component: (
        <SelectTenancyStep
          title="Select tenancy"
          currentStepIndex={currentStepIndex}
          setCurrentStepIndex={setCurrentStepIndex}
          setCompany={setCompany}
          tenants={tenants}
        />
      )
    },
    {
      component: (
        <SignInStep
          apiUrl={apiUrl}
          title="Sign in"
          email={email}
          bearerToken={bearerToken}
          company={company}
        />
      )
    }
  ];

  return (
    <>
      <LoginPageLayout>
        {stepComponents[currentStepIndex]?.component}
      </LoginPageLayout>

      <ToastContainer
        autoClose={false}
        icon={false}
        position="top-right"
        style={{ ['--toastify-toast-width' as any]: '350px' }}
      />
    </>
  );
};

export default LoginPage;
